// Singleton cache to store temporary preview URLs for uploaded files
// This bridges the gap between uploading a file (getting a Blob/Signed URL)
// and the backend persisting the 's3-key' reference.

const previewCache = new Map<string, string>();

/**
 * Registers a temporary URL for a given S3 key.
 * @param key The s3-key identifier (e.g. "s3-key:uuid/uuid.png")
 * @param url The temporary viewable URL (Blob URL or Signed URL)
 */
export function setPreviewUrl(key: string, url: string) {
    previewCache.set(key, url);
}

/**
 * Resolves a URL string. An `s3-key:` value is a stored reference, not an
 * address: only the API can turn one into a signed URL, and every response that
 * carries one is supposed to have done so already. The cache covers the single
 * gap in that — the moment between an upload and the save that persists it.
 *
 * A reference we cannot resolve therefore yields `undefined`, never the
 * reference itself. Handing it back would put `s3-key:...` in an `<img src>`,
 * where the browser reads it as a relative path and fetches
 * `https://app.laudopsi.com/s3-key:customizations/...` — the SPA answers that
 * with index.html, so it renders as a broken image instead of no image.
 *
 * @param urlOrKey The URL or s3-key string
 */
export function resolvePreviewUrl(urlOrKey: string | null | undefined): string | undefined {
    if (!urlOrKey) return undefined;

    if (urlOrKey.startsWith('s3-key:')) {
        return previewCache.get(urlOrKey);
    }

    return urlOrKey;
}
